import { useCustomForm } from "common/hooks/src/api/useCustomForm";
import { Button } from "components/button";
import { CardWithTitle } from "components/card/CardWithTitle";
import { InputField, ResizableInputField } from "components/fields/InputField";
import { SelectField } from "components/fields/SelectField";
import { useCreateClient } from "modules/client/api";
import { CreateClientPayload } from "modules/client/types/client.interface";
import { useGetFamilies } from "modules/family/api";
import { FormEvent, useMemo } from "react";

const CreateClient = () => {
  const { mutate } = useCreateClient();
  const { data: familiesData } = useGetFamilies(undefined, 100);

  const familyOptions = useMemo(() => {
    if (!familiesData?.pages) return [];
    const families = familiesData.pages.flatMap((page) => page.data || []);
    return [
      { value: "", label: "Aucune famille" },
      ...families.map((family) => ({
        value: family._id,
        label: family.name,
      })),
    ];
  }, [familiesData]);

  const { formValues, setFormValues } = useCustomForm<CreateClientPayload>({
    name: "",
    societyName: "",
    email: [{ key: "", value: "" }],
    phoneNumber: "",
    address: "",
    postalCode: "",
    city: "",
    siret: "",
    codeNaf: "",
    tvaCode: "",
    notes: "",
    familyId: "",
  });

  const isSubmitDisabled = formValues.name === "";

  const handleSubmit = (e: FormEvent) => {
    if (isSubmitDisabled) return;
    e.preventDefault();
    mutate({ ...formValues });
  };

  const handleAddEmail = () => {
    setFormValues({
      ...formValues,
      email: [...formValues.email, { key: "", value: "" }],
    });
  };

  const handleRemoveEmail = (index: number) => {
    setFormValues({
      ...formValues,
      email: formValues.email.filter((_, i) => i !== index),
    });
  };

  const updateField = (field: string, value: any) =>
    setFormValues({ ...formValues, [field]: value });

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Identité */}
      <CardWithTitle title="Identité">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputField autoFocus variant="auth" extra="" label="Nom du client *" placeholder="Nom du client" id="name" type="text" value={formValues.name} onChange={(e) => updateField("name", e.target.value)} />
          <InputField variant="auth" extra="" label="Société" placeholder="Nom de la société" id="societyName" type="text" value={formValues.societyName} onChange={(e) => updateField("societyName", e.target.value)} />
          <InputField variant="auth" extra="" label="Code client" placeholder="Auto-généré" id="codeClient" type="text" value={formValues.codeClient} onChange={(e) => updateField("codeClient", e.target.value)} />
          <SelectField label="Famille" options={familyOptions} value={formValues.familyId || ""} onChange={(e) => updateField("familyId", e.target.value || undefined)} />
        </div>
      </CardWithTitle>

      {/* Contact */}
      <CardWithTitle title="Contact">
        <div className="space-y-3">
          {formValues.email.map((email, index) => (
            <div key={index} className="flex items-end gap-3">
              <div className="flex-1">
                <InputField variant="auth" extra="" label={index === 0 ? "Email" : `Email ${index + 1}`} placeholder="email@example.com" id={`email-${index}`} type="email" value={email.value} onChange={(e) => setFormValues({ ...formValues, email: formValues.email.map((item, i) => i === index ? { ...item, value: e.target.value } : item) })} />
              </div>
              <div className="w-32">
                <InputField variant="auth" extra="" label="Type" placeholder="Pro, Perso..." id={`emailKey-${index}`} type="text" value={email.key} onChange={(e) => setFormValues({ ...formValues, email: formValues.email.map((item, i) => i === index ? { ...item, key: e.target.value } : item) })} />
              </div>
              {formValues.email.length > 1 && (
                <button type="button" onClick={() => handleRemoveEmail(index)} className="mb-0.5 rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={handleAddEmail} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-medium text-gray-500 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Ajouter un email
          </button>
        </div>
        <div className="mt-4">
          <InputField variant="auth" extra="" label="Téléphone" placeholder="06 12 34 56 78" id="phoneNumber" type="text" value={formValues.phoneNumber} onChange={(e) => updateField("phoneNumber", e.target.value)} />
        </div>
      </CardWithTitle>

      {/* Adresse */}
      <CardWithTitle title="Adresse">
        <div className="space-y-4">
          <InputField variant="auth" extra="" label="Adresse" placeholder="123 Rue Exemple" id="address" type="text" value={formValues.address} onChange={(e) => updateField("address", e.target.value)} />
          <div className="grid grid-cols-2 gap-4">
            <InputField variant="auth" extra="" label="Code postal" placeholder="75001" id="postalCode" type="text" value={formValues.postalCode} onChange={(e) => updateField("postalCode", e.target.value)} />
            <InputField variant="auth" extra="" label="Ville" placeholder="Paris" id="city" type="text" value={formValues.city} onChange={(e) => updateField("city", e.target.value)} />
          </div>
        </div>
      </CardWithTitle>

      {/* Informations légales */}
      <CardWithTitle title="Informations légales">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputField variant="auth" extra="" label="SIRET" placeholder="123 456 789 01234" id="siret" type="text" value={formValues.siret} onChange={(e) => updateField("siret", e.target.value)} />
          <InputField variant="auth" extra="" label="Code NAF" placeholder="1234Z" id="codeNaf" type="text" value={formValues.codeNaf} onChange={(e) => updateField("codeNaf", e.target.value)} />
          <InputField variant="auth" extra="" label="N° TVA" placeholder="FR12345678901" id="tvaCode" type="text" value={formValues.tvaCode} onChange={(e) => updateField("tvaCode", e.target.value)} />
        </div>
      </CardWithTitle>

      {/* Notes */}
      <CardWithTitle title="Notes">
        <ResizableInputField variant="auth" extra="" label="Notes internes" placeholder="Notes supplémentaires..." id="notes" value={formValues.notes} onChange={(e) => updateField("notes", e.target.value)} />
      </CardWithTitle>

      <div className="flex justify-end pb-4">
        <div className="w-full max-w-[200px]">
          <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
            Créer le client
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateClient;
